import React, {useState} from "react";
import {Alert, Button, ButtonGroup, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {PiCaretDown, PiCaretRight} from "react-icons/pi";
import useAnalysisData from "./useAnalysisData.js";
import AnalysisCard from "./AnalysisCard.jsx";
import YearValueCells from "./YearValueCells.jsx";
import YearHeaderCells from "./YearHeaderCells.jsx";
import SignedPercentValue from "./SignedPercentValue.jsx";
import DrilldownLevel from "./DrilldownLevel.jsx";
import Sparkline from "./Sparkline.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {hasAbility, referenceLabels, sortByArticleTypeOrder, yearRangeExcludingCurrent} from "./format.js";
import {StickyTableArea} from "../StickyFilterBlock.jsx";
import {useQueryState} from "./useQueryState.js";

const EINZELVARIANTEN_AB = 2021;

const AUFSCHLUESSELUNGEN = [
    {wert: "region", label: "Region", hilfe: "Produktlinie aufklappen nach Kontinent → Land → Kunde."},
    {wert: "farbton", label: "Farbton", hilfe: "Produktlinie aufklappen nach Farbton bzw. Artikel → Kunde, je Zeile mit dem Vorjahresvergleich bis zum Stichmonat."},
];

/**
 * Werte für die Sparkline-Spalte "Entwicklungslinie": die vollen Vorjahre
 * im gewählten Zeitraum plus die Hochrechnung als letzter Punkt - bewusst
 * OHNE das angefangene laufende Jahr, siehe Sparkline.jsx.
 */
function sparklineWerte(pastYears, entry) {
    return [...pastYears.map((year) => entry?.years?.[year]), entry?.hr];
}

/**
 * Gesamtsicht: Artikelart × Produktlinie × Jahre, mit Zwischensummen je
 * Artikelart ("… Ergebnis") und Gesamtergebnis, Hochrechnung und Trend -
 * Spaltenreihenfolge und Überschriften wie in der Excel-Datei (fortschritt.md
 * "Sichten und Spalten"). Jede Produktlinien-Zeile lässt sich zum Drilldown
 * Kontinent → Land → Kunde aufklappen, oder - Umschalter „Aufschlüsselung“,
 * Rückmeldung André 28.09.2026, Paket 2 - nach Farbton/Artikel → Kunde.
 */
export default function GesamtTable({filters}) {
    const {meta, params} = filters;
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/gesamt", params);
    const [expanded, setExpanded] = useState({});
    const [aufschluesselung, setAufschluesselung] = useQueryState("aufschluesselung", "region");
    const nachFarbton = aufschluesselung === "farbton";
    // Bis 2020 wurde auf die Übernummer gebucht, nicht auf den Farbton
    // (Buchungsbruch 2020/2021) - ein Farbton zeigt für diese Jahre kaum
    // Umsatz, ohne dass er damals schlecht lief.
    const zeigeUebernummerHinweis = (nachFarbton || (filters.articles || []).length > 0) && Number(filters.yearFrom) < EINZELVARIANTEN_AB;

    const pastYears = yearRangeExcludingCurrent(filters.yearFrom, filters.yearTo, meta?.current_year);
    const labels = referenceLabels(data?.reference, meta);
    const hideCurrent = labels.current === null;
    const hideHr = labels.hr === null;
    const canLinkCustomer = hasAbility(meta, "kunde");
    // sortByArticleTypeOrder sortiert stabil - Zeilen derselben Artikelart
    // bleiben also zusammenhängend, eine Zwischensumme lässt sich damit rein
    // über den Wechsel der Artikelart zur nächsten Zeile erkennen.
    const rows = sortByArticleTypeOrder(data?.rows || []);
    const subtotalsByType = {};
    (data?.subtotals || []).forEach((s) => {
        subtotalsByType[s.article_type] = s;
    });

    const withSubtotals = [];
    rows.forEach((row, index) => {
        withSubtotals.push({kind: "row", row});
        const isLastOfType = rows[index + 1]?.article_type !== row.article_type;
        if (isLastOfType) {
            withSubtotals.push({kind: "subtotal", article_type: row.article_type});
        }
    });

    const rowKey = (row) => `${row.article_type}|${row.product_line}`;

    return (
        <AnalysisCard
            title="Gesamt"
            help="Artikelart und Produktlinie je Jahr, nur Rechnungswerte (Set-Kopfpositionen, ohne Set-Unterpositionen)."
            loading={loading}
            error={error}
            empty={!loading && rows.length === 0}
            actions={
                <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Aufschlüsselung</span>
                    <ButtonGroup size="sm" aria-label="Aufschlüsselung der Produktlinien">
                        {AUFSCHLUESSELUNGEN.map((option) => (
                            <OverlayTrigger key={option.wert} placement="bottom" overlay={<Tooltip>{option.hilfe}</Tooltip>}>
                                <Button
                                    variant={aufschluesselung === option.wert ? "primary" : "outline-secondary"}
                                    aria-pressed={aufschluesselung === option.wert}
                                    onClick={() => {
                                        setAufschluesselung(option.wert);
                                        setExpanded({});
                                    }}
                                >
                                    {option.label}
                                </Button>
                            </OverlayTrigger>
                        ))}
                    </ButtonGroup>
                </div>
            }
        >
            {zeigeUebernummerHinweis && (
                <Alert variant="secondary" className="py-2 px-3 small">
                    Bis 2020 wurde auf die Übernummer gebucht (z. B. 327000 für alle PREMIUM-Farbtöne), erst ab 2021 auf den einzelnen Farbton. Für die Jahre vor 2021 zeigt ein Farbton deshalb kaum Umsatz - der steckt in der Zeile der Übernummer.
                </Alert>
            )}
            <StickyTableArea>
                <Table size="sm" hover className="mb-0 align-middle" style={{position: "relative"}}>
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th className="text-nowrap">Type / Artikelart</th>
                            <th>
                                <HeaderWithHelp help="Produktlinie des Artikels laut Artikelstamm.">Produktlinie</HeaderWithHelp>
                            </th>
                            <YearHeaderCells pastYears={pastYears} currentLabel={labels.current} hrLabel={labels.hr} />
                            <th>
                                <HeaderWithHelp help="Verlauf über die vollen Jahre plus Hochrechnung, eigene Skala je Zeile.">
                                    Entwicklungslinie
                                </HeaderWithHelp>
                            </th>
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegen den Durchschnitt der vier Jahre davor." placement="left">
                                    {labels.trend}
                                </HeaderWithHelp>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {withSubtotals.map((entry, index) => {
                            if (entry.kind === "subtotal") {
                                const subtotal = subtotalsByType[entry.article_type];
                                return (
                                    <tr key={`subtotal-${entry.article_type}-${index}`} className="table-light fw-semibold">
                                        <td colSpan={2} className="text-nowrap">{entry.article_type} Ergebnis</td>
                                        <YearValueCells years={subtotal?.years} current={subtotal?.current} hr={subtotal?.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                        <td><Sparkline values={sparklineWerte(pastYears, subtotal)} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={subtotal?.trend} /></td>
                                    </tr>
                                );
                            }
                            const {row} = entry;
                            const key = rowKey(row);
                            return (
                                <React.Fragment key={key}>
                                    <tr>
                                        <td className="text-nowrap">{row.article_type}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link p-0 me-1 align-baseline"
                                                aria-expanded={Boolean(expanded[key])}
                                                aria-label={expanded[key] ? "Zuklappen" : "Aufklappen"}
                                                onClick={() => setExpanded((current) => ({...current, [key]: !current[key]}))}
                                            >
                                                {expanded[key] ? <PiCaretDown /> : <PiCaretRight />}
                                            </button>
                                            {row.product_line || "Nicht zugeordnet"}
                                        </td>
                                        <YearValueCells years={row.years} current={row.current} hr={row.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                        <td><Sparkline values={sparklineWerte(pastYears, row)} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={row.trend} /></td>
                                    </tr>
                                    {expanded[key] && (
                                        <DrilldownLevel
                                            baseParams={{article_type: row.article_type, product_line: row.product_line, ...params}}
                                            level={nachFarbton ? "article" : "continent"}
                                            pastYears={pastYears}
                                            indent={28}
                                            hideCurrent={hideCurrent}
                                            hideHr={hideHr}
                                            canLinkCustomer={canLinkCustomer}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {data?.total && (
                            <tr className="table-secondary fw-bold">
                                <td colSpan={2} className="text-nowrap">Gesamtergebnis</td>
                                <YearValueCells years={data.total.years} current={data.total.current} hr={data.total.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                <td><Sparkline values={sparklineWerte(pastYears, data.total)} /></td>
                                <td className="text-end text-nowrap"><SignedPercentValue value={data.total.trend} /></td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </StickyTableArea>
        </AnalysisCard>
    );
}
